// SPDX-License-Identifier: AGPL-3.0-only
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Code,
  CopyButton,
  Divider,
  Group,
  List,
  Loader,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';

import { ApiError, api } from '../api/client';
import type {
  Company,
  GatewayArtifact,
  GatewayOnboarding,
  IssuedEnrolmentToken,
  Site,
} from '../api/client';

/**
 * Adding a Gateway, on ONE page.
 *
 * WHAT THIS DOES: creates a pending Gateway enrolment, then tells the operator
 *   exactly which software to run, exactly how to run it, and what to paste in
 *   — and watches until it connects.
 * WHAT DEPENDS ON IT: whether anyone can install Meshvara without being told how
 *   by a person who already knows.
 * WHAT IT IS NOT: a download service. The Server names the artifact; the
 *   registry serves it.
 *
 * # THE PROBLEM THIS PAGE EXISTS TO SOLVE
 *
 * Before it, adding a Gateway meant: issue a token here, go to GitHub or Docker
 * Hub for software, guess which version, find documentation elsewhere for how to
 * run it, come back for the token, and then have no way to tell whether it
 * worked. Four places and a guess.
 *
 * Everything needed is now in one place, in the order it is needed.
 *
 * # CONNECTED MEANS CONNECTED
 *
 * The status below is polled from the Server's live session registry. It does
 * NOT go green because the token was redeemed — a redeemed token means a
 * container started and exchanged a secret, and the WebSocket session that
 * follows can still fail on a firewall or a proxy that will not upgrade.
 *
 * CONNECTING is therefore a real state with a real message, and it is the most
 * informative thing this page can say when something is wrong: the enrolment
 * worked and the network did not.
 *
 * # THE SETUP TOKEN LIVES IN MEMORY, AND ONLY IN MEMORY
 *
 * Same rules as every other plaintext secret in this UI: React state, never
 * localStorage, sessionStorage, IndexedDB, a cookie, the URL, the query string
 * or the fragment, and never logged. There is no second request that can
 * retrieve it — the Server stores a SHA-256 digest — so a refresh loses it and
 * the operator issues another. That is the show-once property working.
 *
 * A `pagehide` listener clears it deliberately, because the browser's
 * back/forward cache can restore a page with its heap intact and put a secret
 * back on screen after the operator believed it was gone.
 */
export function AddGateway() {
  const [issued, setIssued] = useState<IssuedEnrolmentToken | null>(null);
  const [gatewayName, setGatewayName] = useState('');

  // THE SECRET IS CLEARED ON THE WAY OUT, NOT LEFT TO THE BROWSER.
  useEffect(() => {
    const clear = () => setIssued(null);
    window.addEventListener('pagehide', clear);
    return () => window.removeEventListener('pagehide', clear);
  }, []);

  if (!issued) {
    return (
      <NewGatewayForm
        onIssued={(token, name) => {
          setGatewayName(name);
          setIssued(token);
        }}
      />
    );
  }
  return (
    <Onboarding
      issued={issued}
      gatewayName={gatewayName}
      onStartOver={() => setIssued(null)}
    />
  );
}

/**
 * What the operator tells us before anything is created.
 *
 * Name, Site and Platform — the minimum that makes a Gateway findable later and
 * the instructions correct. Platform is a select with one option today rather
 * than a hidden assumption: an operator on a Synology needs to see that this
 * flow is for Docker Desktop, not discover it three steps in.
 */
function NewGatewayForm({
  onIssued,
}: {
  onIssued: (token: IssuedEnrolmentToken, name: string) => void;
}) {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [siteId, setSiteId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [platform, setPlatform] = useState<string | null>('docker-desktop');

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    let live = true;
    api
      .companies()
      .then((result) => {
        if (live) setCompanies(result.items);
      })
      .catch((err: ApiError) => {
        if (live) setError(err);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    if (!companyId) {
      setSites([]);
      setSiteId(null);
      return;
    }
    let live = true;
    api
      .sites(companyId)
      .then((result) => {
        if (live) setSites(result.items);
      })
      .catch((err: ApiError) => {
        if (live) setError(err);
      });
    return () => {
      live = false;
    };
  }, [companyId]);

  const submit = async () => {
    if (!siteId || !companyId) return;
    setBusy(true);
    setError(null);
    try {
      // ONE REQUEST, NEVER RETRIED AUTOMATICALLY. A retry that succeeded twice
      // would mint two live secrets and only one would be on screen.
      const token = await api.issueEnrolmentToken(siteId, companyId, name.trim());
      onIssued(token, name.trim());
    } catch (err) {
      setError(err as ApiError);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <Stack maw={640}>
      <Title order={2}>Add Gateway</Title>
      <Text c="dimmed" size="sm">
        A Gateway is the piece of Meshvara that runs on your network and talks to
        your switches. You will need somewhere to run a container — this flow is
        written for Docker Desktop.
      </Text>

      {error && (
        <Alert color="red" title="That did not work">
          {error.message}
        </Alert>
      )}

      <Card withBorder>
        <Stack>
          <TextInput
            label="Gateway name"
            description="How you will recognise it later. The site and the room are usually enough."
            placeholder="Honolulu office — comms cupboard"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            required
          />
          <Select
            label="Company"
            data={companies.map((c) => ({ value: c.id, label: c.name }))}
            value={companyId}
            onChange={setCompanyId}
            placeholder="Choose a company"
            required
          />
          <Select
            label="Site"
            description="Where this Gateway physically is. Devices it manages belong to the same site."
            data={sites.map((s) => ({ value: s.id, label: s.name }))}
            value={siteId}
            onChange={setSiteId}
            placeholder={companyId ? 'Choose a site' : 'Choose a company first'}
            disabled={!companyId}
            required
          />
          <Select
            label="Platform"
            description="What you will run the Gateway on."
            data={[{ value: 'docker-desktop', label: 'Docker Desktop (Windows or macOS)' }]}
            value={platform}
            onChange={setPlatform}
            required
          />
          <Group justify="flex-end">
            <Button
              onClick={() => void submit()}
              loading={busy}
              disabled={!name.trim() || !siteId || !companyId}
            >
              Create Gateway
            </Button>
          </Group>
        </Stack>
      </Card>
    </Stack>
  );
}

/**
 * The three steps, and the status.
 *
 * Order matters: software, then how to run it, then the secret. An operator who
 * is handed a token first has to keep it safe while they go and find a
 * container image, and a short-lived secret spent waiting is a secret that
 * expires.
 */
function Onboarding({
  issued,
  gatewayName,
  onStartOver,
}: {
  issued: IssuedEnrolmentToken;
  gatewayName: string;
  onStartOver: () => void;
}) {
  const [artifact, setArtifact] = useState<GatewayArtifact | null>(null);
  const [status, setStatus] = useState<GatewayOnboarding | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    let live = true;
    api
      .gatewayArtifact()
      .then((result) => {
        if (live) setArtifact(result);
      })
      .catch((err: ApiError) => {
        if (live) setError(err);
      });
    return () => {
      live = false;
    };
  }, []);

  // POLLED WHILE THE PAGE IS OPEN, AND ONLY WHILE IT IS OPEN.
  //
  // The interval is cleared on unmount, so navigating away stops the requests
  // rather than leaving a timer running against a page nobody is looking at.
  // It also stops once the answer can no longer change: a connected Gateway
  // stays connected, and a revoked token stays revoked.
  const tokenId = issued.token.id;
  const settled = useRef(false);
  const poll = useCallback(async () => {
    if (settled.current) return;
    try {
      const result = await api.gatewayOnboarding(tokenId);
      setStatus(result);
      if (result.state === 'CONNECTED' || result.state === 'TOKEN_REVOKED') {
        settled.current = true;
      }
    } catch (err) {
      setError(err as ApiError);
    }
  }, [tokenId]);

  useEffect(() => {
    void poll();
    const timer = window.setInterval(() => void poll(), 3000);
    return () => window.clearInterval(timer);
  }, [poll]);

  return (
    <Stack maw={760}>
      <Group justify="space-between" align="flex-start">
        <div>
          <Title order={2}>{gatewayName || 'New Gateway'}</Title>
          <Text c="dimmed" size="sm">
            Keep this page open. It will tell you when the Gateway connects.
          </Text>
        </div>
        <StatusBadge status={status} />
      </Group>

      {error && (
        <Alert color="red" title="Could not read the Gateway's status">
          {error.message}
        </Alert>
      )}

      {status?.detail && (
        <Alert color={status.state === 'CONNECTED' ? 'green' : 'blue'} variant="light">
          {status.detail}
          {status.state === 'CONNECTED' && status.agent_version && (
            <Text size="sm" mt="xs">
              Running version {status.agent_version}.
            </Text>
          )}
        </Alert>
      )}

      <StepOne artifact={artifact} />
      <StepTwo artifact={artifact} />
      <StepThree issued={issued} />

      <Group justify="flex-end">
        <Button variant="subtle" onClick={onStartOver}>
          Add another Gateway
        </Button>
      </Group>
    </Stack>
  );
}

/** STEP 1 — which software, exactly. */
function StepOne({ artifact }: { artifact: GatewayArtifact | null }) {
  return (
    <Card withBorder>
      <Title order={4}>Step 1 — Install Meshvara Gateway</Title>
      <Text c="dimmed" size="sm" mb="sm">
        The exact Gateway build that belongs with this Meshvara Server. The
        software is public and needs no account; what makes your Gateway yours is
        the token in step 3.
      </Text>

      {!artifact && <Loader size="sm" />}

      {artifact && !artifact.recorded && (
        <Alert color="orange" title="This Server cannot name its Gateway">
          {artifact.detail}
        </Alert>
      )}

      {artifact?.recorded && (
        <Stack gap="xs">
          <Group gap="xs">
            <Text size="sm" fw={500}>
              Docker Desktop for Windows
            </Text>
          </Group>
          <Group gap="xs">
            <Text size="sm" fw={500}>
              Version
            </Text>
            <Code>{artifact.version}</Code>
            <Text size="sm" fw={500} ml="md">
              Platform
            </Text>
            <Code>{artifact.platform}</Code>
          </Group>

          <Button
            component="a"
            href={dockerHubURL(artifact)}
            target="_blank"
            rel="noopener noreferrer"
            variant="light"
            w="fit-content"
            mt="xs"
          >
            Open Gateway in Docker Hub
          </Button>

          <Group gap="xs" align="flex-start" mt="xs">
            <Text size="xs" fw={500} c="dimmed">
              Digest
            </Text>
            <Code style={{ wordBreak: 'break-all', fontSize: 11 }}>{artifact.digest}</Code>
          </Group>
          <Text size="xs" c="dimmed">
            Shown so you can confirm afterwards that what you pulled is what this
            Server expects. You do not need to type it anywhere — Docker Desktop
            displays it on the image once pulled.
          </Text>
          <Divider my="xs" />
          <Text size="sm" fw={500}>
            Or from inside Docker Desktop
          </Text>
          <List type="ordered" spacing={4} size="sm">
            <List.Item>
              <b>Docker Hub</b> → search <Code>meshvara-gateway</Code>.
            </List.Item>
            <List.Item>
              Choose the tag <Code>{artifact.tag}</Code> and click <b>Pull</b>.
            </List.Item>
          </List>
        </Stack>
      )}
    </Card>
  );
}

/**
 * STEP 2 — how to run it, button by button.
 *
 * # WHY THE CLAIM CODE IS HERE AND NOT ON THE GATEWAY'S OWN PAGE
 *
 * Docker Desktop's Run dialog offers a host PORT and no host address, so the
 * Gateway's setup page is reachable from the whole LAN — and whoever reaches it
 * first could enrol it to THEIR Meshvara Server. The Gateway therefore prints a
 * one-time claim code to its container log and refuses setup without it.
 *
 * Reading that log requires access to the Docker host, which is exactly the
 * person doing the installation. So the instruction to go and read it is part of
 * the flow, not a footnote.
 */
function StepTwo({ artifact }: { artifact: GatewayArtifact | null }) {
  const image = artifact?.tagged_reference ?? 'the image above';
  return (
    <Card withBorder>
      <Title order={4}>Step 2 — Run it in Docker Desktop</Title>
      <List type="ordered" spacing="xs" size="sm" mt="sm">
        <List.Item>
          Open <b>Docker Desktop</b> and go to <b>Images</b>.
        </List.Item>
        <List.Item>
          In <b>Images</b>, find <Code>{image}</Code> — the one you pulled in step 1.
        </List.Item>
        <List.Item>
          Hover the image row and click <b>Run</b>. Expand{' '}
          <b>Optional settings</b>.
        </List.Item>
        <List.Item>
          <b>Container name</b>: <Code>meshvara-gateway</Code>.
        </List.Item>
        <List.Item>
          <b>Ports</b> — <b>Host port</b> <Code>8099</Code> → container{' '}
          <Code>8099</Code>. This is the Gateway&apos;s own setup page, on this
          machine.
        </List.Item>
        <List.Item>
          <b>Volumes</b> — <b>Host path</b> a folder you keep (for example{' '}
          <Code>C:\meshvara\gateway</Code>), <b>Container path</b>{' '}
          <Code>/var/lib/meshvara-gateway</Code>.
          <Text size="xs" c="dimmed" mt={4}>
            Note the <Code>-gateway</Code> on the end — that is the path inside
            the image, and it is where the Gateway&apos;s identity lives. A
            different path leaves the identity in a volume Docker made up, and a
            Gateway that loses it comes back as a stranger the Server will not
            recognise.
          </Text>
        </List.Item>
        <List.Item>Click <b>Run</b>.</List.Item>
        <List.Item>
          Open the container and click <b>Logs</b>. Find the line beginning{' '}
          <Code>SETUP REQUIRED</Code> and copy the <b>setup code</b> beside it.
          You will be asked for it in a moment.
          <Text size="xs" c="dimmed" mt={4}>
            The setup code is printed to the log on purpose: only somebody with
            access to this machine can read it, and it is what stops anyone else
            on your network from claiming this Gateway first.
          </Text>
        </List.Item>
        <List.Item>
          Open <Code>http://localhost:8099</Code> in a browser on that machine.
        </List.Item>
      </List>
    </Card>
  );
}

/** STEP 3 — the secret, last, and only for as long as the page is open. */
function StepThree({ issued }: { issued: IssuedEnrolmentToken }) {
  const expires = new Date(issued.token.expires_at);
  const token = issued.setup_token;

  return (
    <Card withBorder>
      <Title order={4}>Step 3 — Connect it to this Server</Title>
      <Text size="sm" c="dimmed" mb="sm">
        On the Gateway&apos;s page at <Code>http://localhost:8099</Code>, enter
        the setup code from the container log, then paste this token.
      </Text>

      {issued.setup_token_error && (
        <Alert color="orange" title="This Server could not compose a setup token">
          {issued.setup_token_error} You can still enrol this Gateway with an
          enrolment bundle.
        </Alert>
      )}

      {token && (
        <Stack gap="xs">
          <Text size="sm" fw={500}>
            Setup token
          </Text>
          <Code block style={{ wordBreak: 'break-all' }}>
            {token}
          </Code>
          <Group>
            <CopyButton value={token}>
              {({ copied, copy }) => (
                <Button size="xs" variant="light" onClick={copy}>
                  {copied ? 'Copied' : 'Copy token'}
                </Button>
              )}
            </CopyButton>
            <Text size="xs" c="dimmed">
              Expires {expires.toLocaleString()}
            </Text>
          </Group>
          <Alert color="yellow" variant="light" mt="xs">
            This token is shown once and cannot be retrieved. It carries the
            address of this Server and the key the Gateway will pin before it
            trusts anything — which is why it must reach the Gateway in your
            hands rather than over the connection it is about to make. If you
            lose it, add another Gateway and get a new one.
          </Alert>
        </Stack>
      )}
    </Card>
  );
}

/**
 * The status, in the four words that mean different things.
 *
 * CONNECTING is not a lesser CONNECTED — it means the token worked and the
 * session did not, which points at the network. Giving it its own colour and
 * its own sentence is what makes that visible.
 */
function StatusBadge({ status }: { status: GatewayOnboarding | null }) {
  if (!status) return <Badge color="gray">Checking</Badge>;

  switch (status.state) {
    case 'CONNECTED':
      return <Badge color="green">Connected</Badge>;
    case 'CONNECTING':
      return <Badge color="yellow">Connecting</Badge>;
    case 'TOKEN_EXPIRED':
      return <Badge color="red">Token expired</Badge>;
    case 'TOKEN_REVOKED':
      return <Badge color="red">Token revoked</Badge>;
    default:
      return (
        <Group gap="xs">
          <Loader size="xs" />
          <Badge color="blue">Waiting for Gateway</Badge>
        </Group>
      );
  }
}

/**
 * The repository half of an image reference — what Docker Desktop's My Hub
 * actually lists.
 *
 * My Hub shows repositories by owner and name, not by registry host: an operator
 * looking for `docker.io/mattdarnell/meshvara-gateway` sees `mattdarnell` in the
 * sidebar and `meshvara-gateway` under it. Printing the full reference there
 * would send them looking for a row that does not exist under that name.
 */
function repositoryOf(artifact: GatewayArtifact): string {
  const image = artifact.image ?? '';
  return image.replace(/^docker\.io\//, '');
}

/**
 * A link straight to the repository page on Docker Hub.
 *
 * The operator should not have to know that `docker.io/owner/name` becomes
 * `hub.docker.com/r/owner/name`, and should certainly not have to paste a
 * reference into a search box to find software this Server already knows the
 * exact identity of. One button, one destination.
 *
 * Only meaningful for Docker Hub. A self-hosted or third-party registry has no
 * predictable web page, so the button is not rendered for one — an operator
 * following a link to a 404 is worse off than one reading a reference.
 */
function dockerHubURL(artifact: GatewayArtifact): string {
  return 'https://hub.docker.com/r/' + repositoryOf(artifact);
}
